import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, DollarSign } from "lucide-react";
import { format, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface MakeOfferDialogProps {
  propertyId: string;
  sellerId: string;
  propertyAddress: string;
  listingPrice: number;
  isAuthenticated: boolean;
}

interface ExistingOffer {
  id: string;
  status: string;
  offer_amount: number;
  created_at: string;
}

export const MakeOfferDialog = ({
  propertyId,
  sellerId,
  propertyAddress,
  listingPrice,
  isAuthenticated,
}: MakeOfferDialogProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");
  const [closeDate, setCloseDate] = useState<Date>();
  const [buyerNotes, setBuyerNotes] = useState("");
  const [existingOffer, setExistingOffer] = useState<ExistingOffer | null>(null);

  // Check for existing offers when component mounts or authentication changes
  useEffect(() => {
    const checkExistingOffer = async () => {
      if (!isAuthenticated || !propertyId) return;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: buyerProfile } = await supabase
        .from('buyer_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!buyerProfile) return;

      const { data } = await (supabase as any)
        .from('property_offers')
        .select('id, status, offer_amount, created_at')
        .eq('property_id', propertyId)
        .eq('buyer_id', buyerProfile.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (data) {
        setExistingOffer(data);
      }
    };
    
    checkExistingOffer();
  }, [propertyId, isAuthenticated]);

  const handleSubmit = async () => {
    if (!offerAmount || !closeDate) {
      toast.error("Please fill in all required fields");
      return;
    }

    const amount = parseFloat(offerAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid offer amount");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const { data, error } = await supabase.functions.invoke('submit-property-offer', {
        body: {
          propertyId,
          offerAmount: amount,
          closeDate: closeDate.toISOString(),
          buyerNotes: buyerNotes || null
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success("Offer submitted successfully!");
        
        // Refresh offer status to update button
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: buyerProfile } = await supabase
            .from('buyer_profiles')
            .select('id')
            .eq('user_id', user.id)
            .single();

          if (buyerProfile) {
            const { data: newOffer } = await (supabase as any)
              .from('property_offers')
              .select('id, status, offer_amount, created_at')
              .eq('property_id', propertyId)
              .eq('buyer_id', buyerProfile.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (newOffer) {
              setExistingOffer(newOffer);
            }
          }
        }

        setOpen(false);
        setOfferAmount("");
        setCloseDate(undefined);
        setBuyerNotes("");
      } else {
        throw new Error(data.error || "Failed to submit offer");
      }
    } catch (error: any) {
      console.error('Error submitting offer:', error);
      toast.error("We couldn't submit your offer. Please try again or contact support.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Button 
        className="w-full bg-godyspo-gold text-black hover:bg-godyspo-gold/90"
        onClick={() => navigate('/buyer/login')}
      >
        <DollarSign className="h-4 w-4 mr-2" />
        Sign In to Make Offer
      </Button>
    );
  }

  // Determine button state based on existing offer
  const getButtonConfig = () => {
    if (!existingOffer) {
      return {
        text: "Make Offer",
        disabled: false,
        className: "w-full bg-godyspo-gold text-black hover:bg-godyspo-gold/90"
      };
    }

    switch (existingOffer.status) {
      case 'pending':
        return {
          text: `Offer Pending: ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(existingOffer.offer_amount)}`,
          disabled: true,
          className: "w-full bg-muted text-muted-foreground cursor-not-allowed"
        };
      case 'accepted':
        return {
          text: "Offer Accepted ✓",
          disabled: true,
          className: "w-full bg-green-600 text-white cursor-not-allowed"
        };
      case 'rejected':
      case 'withdrawn':
        return {
          text: "Make Another Offer",
          disabled: false,
          className: "w-full bg-godyspo-gold text-black hover:bg-godyspo-gold/90"
        };
      case 'cancelled':
        return {
          text: "Deal Fell Through - Make New Offer",
          disabled: false,
          className: "w-full bg-godyspo-gold text-black hover:bg-godyspo-gold/90"
        };
      default:
        return {
          text: "Make Offer",
          disabled: false,
          className: "w-full bg-godyspo-gold text-black hover:bg-godyspo-gold/90"
        };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={buttonConfig.className} disabled={buttonConfig.disabled}>
          <DollarSign className="h-4 w-4 mr-2" />
          {buttonConfig.text}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Submit an Offer</DialogTitle>
          <DialogDescription>
            {propertyAddress}
            <br />
            <span className="text-sm text-muted-foreground">
              Listing Price: ${listingPrice.toLocaleString()}
            </span>
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="offer-amount">Offer Amount *</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="offer-amount"
                type="number"
                placeholder="Enter your offer"
                className="pl-9"
                value={offerAmount}
                onChange={(e) => setOfferAmount(e.target.value)}
                min="0"
                step="1000"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="close-date">Desired Close Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="close-date"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !closeDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {closeDate ? format(closeDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={closeDate}
                  onSelect={setCloseDate}
                  disabled={(date) => {
                    const tomorrow = addDays(new Date(), 1);
                    const maxDate = addDays(new Date(), 180);
                    return date < tomorrow || date > maxDate;
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              Must be between tomorrow and 180 days from now
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="buyer-notes">Additional Notes (Optional)</Label>
            <Textarea
              id="buyer-notes"
              placeholder="Share any additional information or conditions..."
              value={buyerNotes}
              onChange={(e) => setBuyerNotes(e.target.value)}
              maxLength={500}
              rows={4}
            />
            <p className="text-xs text-muted-foreground text-right">
              {buyerNotes.length}/500
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !offerAmount || !closeDate}
            className="bg-godyspo-gold text-black hover:bg-godyspo-gold/90"
          >
            {isSubmitting ? "Submitting..." : "Submit Offer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
